import { getMyProfile, saveMyProfile, uploadBrandImage, logoUrl } from '@/lib/data/branding';
import { companyName, tagline, logoPath, photoPath } from '@/lib/branding';
import { brandImageFilename, downloadImage } from '@/lib/brand-download';
import { brandLockup, BRAND_IMAGE_GUIDANCE } from '@/lib/brand-lockup';
import { BRAND_FONTS, brandFont, brandColor, colorWarning } from '@/lib/brand-type';
import { ensureBrandFont } from '@/lib/brand-font-loader';
import { esc, setPageTitle } from '@/lib/dom';
import { toastError, toastSuccess } from '@/lib/toast';
import { getShell } from '@/lib/shell-handle';
import type { DesignerProfileRow } from '@/types/database';
import { fetchMyMembership, type MyMembership } from '@/lib/data/firms';
import { fetchTeam, inviteMember, removeMember, resendInvite, setMemberRole } from '@/lib/data/team';
import { saveErrorMessage } from '@/lib/modal-dirty';
import { clearExportBranding } from '@/lib/export';
import {
  seatSummary, seatsLeft, memberStatus, inviteProblem, rowActions, removeConfirmText,
  canManageFirm, roleChangeConfirm, ADMIN_EXPLAINER, type TeamMember,
} from '@/lib/team';
import { getSession } from '@/lib/auth';

/**
 * Settings — branding (Phase 18) and the team (Phase 24).
 *
 * Branding shows on the app's top-left, the client share header and PDF
 * exports. Deliberately NOT the contractor portal (decisions log #7).
 */
export async function renderSettings(root: HTMLElement): Promise<void> {
  root.innerHTML = `<section class="page"><div class="loading">Loading settings…</div></section>`;
  setPageTitle('Settings');
  getShell()?.setContext({ kind: 'global', active: 'settings' });
  try {
    const [profile, membership, team, session] = await Promise.all([
      getMyProfile(),
      fetchMyMembership().catch(() => null),
      fetchTeam().catch(() => [] as TeamMember[]),
      getSession().catch(() => null),
    ]);
    paint(root, profile, membership, team, session?.user.id ?? null);
  } catch (err) {
    root.innerHTML = `<section class="page"><div class="auth-error">${
      esc(err instanceof Error ? err.message : 'Failed to load settings')
    }</div></section>`;
  }
}

function paint(
  root: HTMLElement, profile: DesignerProfileRow | null,
  membership: MyMembership | null, team: TeamMember[], viewerId: string | null,
): void {
  const reload = (): void => { void renderSettings(root); };
  const path = logoPath(profile);
  // Owners and admins change branding and manage the team (F7, widened to
  // admins in 0042). The database enforces it; this just avoids offering
  // buttons that would fail. A failed membership lookup (null) keeps the old
  // behavior: controls shown.
  const canManage = membership ? canManageFirm(membership.role) : true;
  const viewerRole = membership?.role ?? null;
  const photo = photoPath(profile);
  const chosenFont = brandFont(profile?.brand_font).key;
  const chosenColor = brandColor(profile?.brand_color);
  // What the preview renders. Rebuilt from the live controls on every edit, so
  // the pane below the button always shows what Save would produce.
  const previewBranding = { ...(profile ?? {}), firm_name: membership?.firm.name ?? null };
  const limit = membership?.firm.seat_limit ?? 6;

  root.innerHTML = `
    <section class="page">
      <div class="page-head"><h1>Settings</h1></div>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Branding</h3></div></div>
        <p class="muted" style="margin-bottom:18px">
          Shown in the app header, on client share links, and on printed exports.
          Your contractor portal stays unbranded.
        </p>

        <div class="branding-edit">
          <div class="branding-image">
            ${path
              ? `<img class="branding-image-preview" src="${esc(logoUrl(path))}" alt="Current logo" />`
              : `<div class="branding-logo-empty">No logo</div>`}
            <div class="branding-image-label">Firm logo</div>
            <div class="branding-logo-actions">
              ${canManage ? `<button class="btn-link" data-action="upload-logo">${path ? 'Replace' : 'Upload'}</button>` : ''}
              ${path ? `<button class="btn-link" data-action="download-logo">Download</button>` : ''}
              ${canManage && path ? `<button class="btn-link btn-danger-link" data-action="remove-logo">Remove</button>` : ''}
            </div>
            <p class="muted small branding-image-hint">${esc(BRAND_IMAGE_GUIDANCE.logo.text)}</p>
          </div>

          <div class="branding-image">
            ${photo
              ? `<img class="branding-image-preview is-round" src="${esc(logoUrl(photo))}" alt="Your photo" />`
              : `<div class="branding-logo-empty is-round">No photo</div>`}
            <div class="branding-image-label">Your photo</div>
            <div class="branding-logo-actions">
              ${canManage ? `<button class="btn-link" data-action="upload-photo">${photo ? 'Replace' : 'Upload'}</button>` : ''}
              ${photo ? `<button class="btn-link" data-action="download-photo">Download</button>` : ''}
              ${canManage && photo ? `<button class="btn-link btn-danger-link" data-action="remove-photo">Remove</button>` : ''}
            </div>
            <p class="muted small branding-image-hint">${esc(BRAND_IMAGE_GUIDANCE.photo.text)}</p>
          </div>

          <div class="branding-fields">
            <label class="field">
              <span>Company name</span>
              <input id="brand-company" type="text" value="${esc(profile?.company_name ?? '')}"
                     placeholder="${esc(membership?.firm.name ?? '')}" ${canManage ? '' : 'disabled'} />
            </label>
            <label class="field">
              <span>Tagline</span>
              <input id="brand-tagline" type="text" value="${esc(profile?.tagline ?? '')}"
                     placeholder="Interior design studio" ${canManage ? '' : 'disabled'} />
            </label>
            <div class="branding-type">
              <label class="field">
                <span>Name typeface</span>
                <select id="brand-font" ${canManage ? '' : 'disabled'}>
                  ${BRAND_FONTS.map((f) => `<option value="${esc(f.key)}"${
                    f.key === chosenFont ? ' selected' : ''
                  }>${esc(f.label)}</option>`).join('')}
                </select>
              </label>
              <label class="field field-color">
                <span>Name color</span>
                <input id="brand-color" type="color" value="${esc(chosenColor)}" ${canManage ? '' : 'disabled'} />
              </label>
            </div>
            <p class="muted small" data-font-note>${esc(brandFont(chosenFont).note)}</p>
            <p class="auth-error branding-color-warning" data-color-warning ${
              colorWarning(chosenColor) ? '' : 'hidden'}>${esc(colorWarning(chosenColor) ?? '')}</p>
            ${canManage
              ? `<button class="btn-primary" data-action="save-branding">Save branding</button>`
              : `<p class="muted">Only your firm’s owner or an admin can change branding.</p>`}
          </div>
        </div>

        <div class="branding-preview">
          <div class="muted" style="font-size:11px;letter-spacing:.14em;text-transform:uppercase;margin-bottom:8px">
            Preview — the top left of every page
          </div>
          <div class="branding-preview-bar" data-brand-preview>
            ${brandLockup({
              branding: previewBranding,
              firmName: membership?.firm.name,
              variant: 'nav',
              logoUrl: path ? logoUrl(path) : null,
              photoUrl: photo ? logoUrl(photo) : null,
              tagSuffix: tagline(previewBranding) ? null : 'Project Hub',
            })}
            <div class="branding-preview-crumb">Westlake Modern Home</div>
          </div>
          <p class="muted small" style="margin-top:8px">
            Your client sees the same name, logo and photo on their share page and on printed pages.
          </p>
        </div>
      </section>

      ${membership ? `
      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left"><h3>Team</h3></div>
          <span class="muted">${esc(seatSummary(team.length, limit))}</span>
        </div>
        <ul class="team-list">
          ${team.map((m) => `
            <li class="team-row">
              <span class="team-email">${esc(m.email)}${m.user_id === viewerId ? ' <span class="muted">(you)</span>' : ''}</span>
              <span class="team-row-right">
                <span class="muted">${esc(memberStatus(m))}</span>
                ${(() => {
                  const a = rowActions(m, { userId: viewerId, role: viewerRole });
                  return `${a.canChangeRole && a.nextRole ? `<button class="btn-link" data-action="set-role" data-user="${esc(m.user_id)}" data-role="${esc(a.nextRole)}">${
                    a.nextRole === 'admin' ? 'Make admin' : 'Make member'}</button>` : ''}${
                    a.canResend ? `<button class="btn-link" data-action="resend-invite" data-user="${esc(m.user_id)}">Resend</button>` : ''}${
                    a.canRemove ? `<button class="btn-link btn-danger-link" data-action="remove-member" data-user="${esc(m.user_id)}">${m.joined ? 'Remove' : 'Cancel invite'}</button>` : ''}`;
                })()}
              </span>
            </li>`).join('')}
        </ul>
        ${canManage ? `
          <form class="team-invite" data-invite-form novalidate>
            <label class="field">
              <span>Invite someone by email</span>
              <input type="email" name="email" autocomplete="off" placeholder="name@example.com"
                     ${seatsLeft(team.length, limit) === 0 ? 'disabled' : ''} />
            </label>
            <label class="field">
              <span>Join as</span>
              <select name="role" ${seatsLeft(team.length, limit) === 0 ? 'disabled' : ''}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="submit" class="btn-primary" ${seatsLeft(team.length, limit) === 0 ? 'disabled' : ''}>Send invite</button>
            <div class="auth-error" data-invite-error hidden></div>
            <p class="muted small">They’ll get an email with a link to choose a password. ${esc(ADMIN_EXPLAINER)}</p>
          </form>` : ''}
      </section>` : ''}
    </section>`;

  const memberById = new Map(team.map((m) => [m.user_id, m] as const));

  root.querySelectorAll<HTMLButtonElement>('[data-action="remove-member"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const m = memberById.get(btn.dataset.user ?? '');
      if (!m || !confirm(removeConfirmText(m))) return;
      btn.disabled = true;
      try {
        await removeMember(m.user_id);
        toastSuccess(m.joined ? `${m.email} removed` : `Invitation to ${m.email} cancelled`);
        reload();
      } catch (err) {
        btn.disabled = false;
        toastError(err instanceof Error ? err.message : 'That didn’t work.');
      }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action="set-role"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const m = memberById.get(btn.dataset.user ?? '');
      const to = btn.dataset.role === 'admin' ? 'admin' : 'member';
      if (!m || !confirm(roleChangeConfirm(m, to))) return;
      btn.disabled = true;
      try {
        await setMemberRole(m.user_id, to);
        toastSuccess(to === 'admin' ? `${m.email} is now an admin` : `${m.email} is now a member`);
        reload();
      } catch (err) {
        btn.disabled = false;
        // Supabase errors are plain objects, not Errors (CLAUDE.md).
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action="resend-invite"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const m = memberById.get(btn.dataset.user ?? '');
      if (!m) return;
      btn.disabled = true;
      btn.textContent = 'Sending…';
      try {
        await resendInvite(m.user_id);
        toastSuccess(`Invitation sent again to ${m.email}`);
        btn.textContent = 'Sent';
      } catch (err) {
        btn.disabled = false;
        btn.textContent = 'Resend';
        toastError(err instanceof Error ? err.message : 'That didn’t work.');
      }
    });
  });

  root.querySelectorAll<HTMLFormElement>('[data-invite-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector<HTMLInputElement>('input[name="email"]')!;
      const errBox = form.querySelector<HTMLElement>('[data-invite-error]')!;
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      errBox.hidden = true;
      const problem = inviteProblem(input.value, team, team.length, limit);
      if (problem) { errBox.textContent = problem; errBox.hidden = false; return; }
      button.disabled = true;
      button.textContent = 'Sending…';
      try {
        const roleSel = form.querySelector<HTMLSelectElement>('select[name="role"]');
        await inviteMember(input.value, roleSel?.value === 'admin' ? 'admin' : 'member');
        toastSuccess(`Invitation sent to ${input.value.trim()}`);
        reload();
      } catch (err) {
        errBox.textContent = err instanceof Error ? err.message : 'The invitation couldn’t be sent.';
        errBox.hidden = false;
        button.disabled = false;
        button.textContent = 'Send invite';
      }
    });
  });

  root.querySelectorAll('[data-action="save-branding"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const company = root.querySelector<HTMLInputElement>('#brand-company')!.value.trim();
      const tag = root.querySelector<HTMLInputElement>('#brand-tagline')!.value.trim();
      const font = root.querySelector<HTMLSelectElement>('#brand-font')?.value ?? null;
      const color = root.querySelector<HTMLInputElement>('#brand-color')?.value ?? null;
      try {
        // Empty means "use the built-in", so store null rather than ''.
        await saveMyProfile({
          company_name: company || null,
          tagline: tag || null,
          brand_font: brandFont(font).key,
          brand_color: brandColor(color),
        });
        // Printed exports cache the branding for the session (see export.ts).
        clearExportBranding();
        toastSuccess('Branding saved');
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not save branding');
      }
    });
  });

  // The logo and the photo upload the same way into the same folder; only the
  // column they land in differs.
  const pickAndSave = (column: 'logo_storage_path' | 'photo_storage_path', done: string): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        await saveMyProfile({ [column]: await uploadBrandImage(file) });
        clearExportBranding();
        toastSuccess(done);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    });
    input.click();
  };

  /**
   * Clears the reference only. The file is left in storage — it's tiny, and
   * orphaning it is safer than deleting something an export a client already
   * downloaded still points at.
   */
  const clearImage = async (column: 'logo_storage_path' | 'photo_storage_path'): Promise<void> => {
    try {
      await saveMyProfile({ [column]: null });
      clearExportBranding();
      reload();
    } catch (err) {
      toastError(saveErrorMessage(err, navigator.onLine));
    }
  };

  // Saving the file back out. Offered to anyone who can see it — a member who
  // may not change the branding may still need the logo for an invoice.
  const saveImage = (storagePath: string, kind: 'logo' | 'photo'): void => {
    const filename = brandImageFilename(companyName(profile, membership?.firm.name), kind, storagePath);
    void downloadImage(logoUrl(storagePath), filename).then((how) => {
      if (how === 'opened') toastSuccess('Opened in a new tab — use “Save image as” from there.');
    }).catch(() => toastError('Could not download that image.'));
  };
  if (path) {
    root.querySelectorAll('[data-action="download-logo"]').forEach((el) => {
      el.addEventListener('click', () => saveImage(path, 'logo'));
    });
  }
  if (photo) {
    root.querySelectorAll('[data-action="download-photo"]').forEach((el) => {
      el.addEventListener('click', () => saveImage(photo, 'photo'));
    });
  }

  root.querySelectorAll('[data-action="upload-logo"]').forEach((el) => {
    el.addEventListener('click', () => pickAndSave('logo_storage_path', 'Logo updated'));
  });
  root.querySelectorAll('[data-action="remove-logo"]').forEach((el) => {
    el.addEventListener('click', () => { void clearImage('logo_storage_path'); });
  });
  root.querySelectorAll('[data-action="upload-photo"]').forEach((el) => {
    el.addEventListener('click', () => pickAndSave('photo_storage_path', 'Photo updated'));
  });
  root.querySelectorAll('[data-action="remove-photo"]').forEach((el) => {
    el.addEventListener('click', () => { void clearImage('photo_storage_path'); });
  });

  // ── The preview follows the controls, before anything is saved ───────────
  // A preview that only updates on Save cannot answer "what will this look
  // like?", which is the entire question it exists for.
  const previewBar = root.querySelector<HTMLElement>('[data-brand-preview]');
  const drawPreview = (): void => {
    if (!previewBar) return;
    const font = root.querySelector<HTMLSelectElement>('#brand-font')?.value ?? null;
    const color = root.querySelector<HTMLInputElement>('#brand-color')?.value ?? null;
    const live = {
      ...previewBranding,
      company_name: root.querySelector<HTMLInputElement>('#brand-company')?.value ?? null,
      tagline: root.querySelector<HTMLInputElement>('#brand-tagline')?.value ?? null,
      brand_font: font,
      brand_color: color,
    };
    ensureBrandFont(font);
    const crumb = previewBar.querySelector('.branding-preview-crumb')?.outerHTML ?? '';
    previewBar.innerHTML = brandLockup({
      branding: live,
      firmName: membership?.firm.name,
      variant: 'nav',
      logoUrl: path ? logoUrl(path) : null,
      photoUrl: photo ? logoUrl(photo) : null,
      tagSuffix: tagline(live) ? null : 'Project Hub',
    }) + crumb;

    const note = root.querySelector<HTMLElement>('[data-font-note]');
    if (note) note.textContent = brandFont(font).note;
    const warn = root.querySelector<HTMLElement>('[data-color-warning]');
    if (warn) {
      const message = colorWarning(color);
      warn.textContent = message ?? '';
      warn.hidden = message === null;
    }
  };
  for (const id of ['#brand-company', '#brand-tagline', '#brand-font', '#brand-color']) {
    root.querySelectorAll<HTMLInputElement>(id).forEach((el) => {
      el.addEventListener('input', drawPreview);
      el.addEventListener('change', drawPreview);
    });
  }
  // The saved font may be one index.html doesn't carry.
  ensureBrandFont(chosenFont);
}
